import { cx } from "../lib/format";
import type { AgentStatus, CallStatus, DocumentStatus } from "../lib/types";

export type DotTone = "success" | "warning" | "danger" | "muted" | "bright";

const TONES: Record<DotTone, string> = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  muted: "bg-muted",
  bright: "bg-bright",
};

export interface StatusDotProps {
  tone: DotTone;
  label?: string;
  /** Soft pulse for in-flight states (respects reduced motion via CSS). */
  pulse?: boolean;
  className?: string;
}

export function StatusDot({ tone, label, pulse = false, className }: StatusDotProps) {
  return (
    <span className={cx("inline-flex items-center gap-1.5", className)}>
      <span
        aria-hidden="true"
        className={cx("h-1.5 w-1.5 rounded-full", TONES[tone], pulse && "animate-pulse")}
      />
      {label && <span className="text-xs text-body">{label}</span>}
    </span>
  );
}

/** Canonical tone mapping for agent status. */
export function agentStatusTone(status: AgentStatus): DotTone {
  switch (status) {
    case "active":
      return "success";
    case "draft":
      return "muted";
    case "disabled":
      return "warning";
  }
}

/** Canonical tone mapping for call status. */
export function callStatusTone(status: CallStatus): DotTone {
  switch (status) {
    case "in_progress":
      return "bright";
    case "completed":
      return "success";
    case "initiating":
      return "muted";
    case "no_answer":
      return "warning";
    case "failed":
      return "danger";
  }
}

/** Canonical tone mapping for document ingestion status. */
export function documentStatusTone(status: DocumentStatus): DotTone {
  switch (status) {
    case "ready":
      return "success";
    case "pending":
      return "muted";
    case "processing":
      return "bright";
    case "failed":
      return "danger";
  }
}
